import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ClipboardCheck,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Home,
  Calendar,
  CloudLightning,
  Droplets,
  Eye,
  Layers,
  ThermometerSun,
  Phone,
  Mail,
  MapPin,
  User,
  Shield,
  Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "roof_age",
    question: "How old is your current roof?",
    icon: Calendar,
    options: [
      { value: "0-10", label: "Less than 10 years", score: 0 },
      { value: "10-15", label: "10-15 years", score: 1 },
      { value: "15-20", label: "15-20 years", score: 2 },
      { value: "20+", label: "Over 20 years", score: 3 },
      { value: "unknown", label: "I'm not sure", score: 2 }
    ]
  },
  {
    id: "recent_storm",
    question: "Has your area experienced a significant storm recently?",
    icon: CloudLightning,
    options: [
      { value: "yes_hail", label: "Yes, with hail", score: 3 },
      { value: "yes_wind", label: "Yes, high winds", score: 2 },
      { value: "yes_both", label: "Yes, both hail and wind", score: 4 },
      { value: "no", label: "No recent storms", score: 0 },
      { value: "unsure", label: "I'm not sure", score: 1 }
    ]
  },
  {
    id: "visible_damage",
    question: "Have you noticed any visible damage to your roof?",
    icon: Eye,
    options: [
      { value: "missing_shingles", label: "Missing or damaged shingles", score: 3 },
      { value: "granules", label: "Granules in gutters", score: 2 },
      { value: "dents", label: "Dents on vents or flashing", score: 2 },
      { value: "sagging", label: "Sagging or uneven areas", score: 4 },
      { value: "none", label: "No visible damage", score: 0 },
      { value: "havent_checked", label: "Haven't checked", score: 1 }
    ]
  },
  {
    id: "interior_signs",
    question: "Have you noticed any interior signs of roof problems?",
    icon: Droplets,
    options: [
      { value: "water_stains", label: "Water stains on ceilings", score: 3 },
      { value: "active_leak", label: "Active leaking during rain", score: 4 },
      { value: "attic_moisture", label: "Moisture or mold in attic", score: 3 },
      { value: "peeling_paint", label: "Peeling paint on walls", score: 2 },
      { value: "none", label: "No interior issues", score: 0 }
    ]
  },
  {
    id: "gutter_condition",
    question: "What's the condition of your gutters and downspouts?",
    icon: Layers,
    options: [
      { value: "dented", label: "Dented or damaged", score: 2 },
      { value: "granules", label: "Full of granules", score: 2 },
      { value: "pulling_away", label: "Pulling away from house", score: 2 },
      { value: "good", label: "In good condition", score: 0 },
      { value: "unsure", label: "Haven't checked", score: 1 }
    ]
  },
  {
    id: "energy_bills",
    question: "Have your heating/cooling bills increased noticeably?",
    icon: ThermometerSun,
    options: [
      { value: "significant", label: "Yes, significantly higher", score: 2 },
      { value: "slight", label: "Slightly higher", score: 1 },
      { value: "same", label: "About the same", score: 0 },
      { value: "unsure", label: "Not sure", score: 0 }
    ]
  },
  {
    id: "insurance_claim",
    question: "Have you filed an insurance claim for roof damage before?",
    icon: Shield,
    options: [
      { value: "yes_approved", label: "Yes, and it was approved", score: 0 },
      { value: "yes_denied", label: "Yes, but it was denied", score: 1 },
      { value: "considering", label: "Considering filing one now", score: 2 },
      { value: "no", label: "No, never filed a claim", score: 0 }
    ]
  }
];

const getAssessmentResult = (score) => {
  if (score >= 15) {
    return {
      level: "urgent",
      title: "Urgent: Professional Inspection Recommended",
      description: "Based on your responses, your roof shows multiple signs that warrant immediate professional attention. Delaying inspection could lead to further damage and higher repair costs.",
      color: "red",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      iconColor: "text-red-600",
      icon: AlertCircle,
      recommendations: [
        "Schedule a professional roof inspection as soon as possible",
        "Document any visible damage with photos",
        "Contact your insurance company to report potential damage",
        "Avoid going on the roof yourself - leave it to professionals"
      ]
    };
  } else if (score >= 8) {
    return {
      level: "moderate",
      title: "Moderate Concern: Inspection Advised",
      description: "Your responses indicate some potential issues that should be evaluated by a professional. An inspection can identify problems before they become costly repairs.",
      color: "orange",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      iconColor: "text-orange-600",
      icon: AlertTriangle,
      recommendations: [
        "Schedule a professional inspection within the next few weeks",
        "Monitor for any changes or new damage",
        "Check your attic for signs of moisture after rain",
        "Review your insurance policy coverage"
      ]
    };
  } else {
    return {
      level: "low",
      title: "Low Risk: Routine Maintenance Recommended",
      description: "Based on your responses, your roof appears to be in reasonable condition. Regular maintenance and periodic inspections will help keep it that way.",
      color: "green",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      iconColor: "text-green-600",
      icon: CheckCircle,
      recommendations: [
        "Continue regular roof maintenance",
        "Schedule an inspection every 2-3 years",
        "Check your roof after major storms",
        "Keep gutters clean and clear of debris"
      ]
    };
  }
};

export default function RoofAssessment() {
  useSEO();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    city: ""
  });

  const totalQuestions = QUESTIONS.length;
  const progress = ((currentStep + 1) / totalQuestions) * 100;

  const calculateScore = () => {
    return Object.values(answers).reduce((total, answer) => total + (answer?.score || 0), 0);
  };

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleNext = () => {
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Submit as a lead with assessment data
      await axios.post(`${API}/leads`, {
        name: contactInfo.name,
        email: contactInfo.email,
        phone: contactInfo.phone,
        city: contactInfo.city,
        address: "Assessment Lead",
        situation: "roof-assessment",
        message: `Roof Assessment Results:\n${Object.entries(answers).map(([q, a]) => `${q}: ${a.label}`).join('\n')}\nTotal Score: ${calculateScore()}`
      });

      setContactSubmitted(true);
      setShowResults(true);
      toast.success("Assessment complete! Check your results below.");
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') {
          console.error("Assessment submission error:", err);
        }
      }
      // Still show results even if submission fails
      setShowResults(true);
      toast.error("Could not save your info, but here are your results.");
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = QUESTIONS[currentStep];
  const currentAnswer = answers[currentQuestion?.id];
  const result = getAssessmentResult(calculateScore());

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Roof Damage Assessment Tool",
    "description": "Free interactive tool to assess potential roof damage and determine if you need a professional inspection.",
    "applicationCategory": "HomeImprovement",
    "operatingSystem": "Web Browser",
    "provider": {
      "@type": "Organization",
      "name": "StormCareMN",
      "url": "https://stormcaremn.com"
    }
  };

  return (
    <div data-testid="roof-assessment-page" className="min-h-screen bg-gray-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />

      {/* Header */}
      <section className="bg-navy py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <nav className="flex items-center justify-center gap-2 text-gray-400 text-sm mb-6">
            <Link to="/" className="hover:text-orange">Home</Link>
            <span>/</span>
            <span className="text-gray-300">Roof Assessment</span>
          </nav>

          <div className="inline-flex items-center gap-2 bg-orange/20 text-orange text-sm font-medium px-4 py-2 rounded-full mb-6">
            <ClipboardCheck className="w-4 h-4" />
            Free Assessment Tool
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Roof Damage Assessment
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Answer a few quick questions to help determine if your roof needs professional inspection. 
            Takes about 2 minutes.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {!showResults ? (
          !showContactForm ? (
            /* Quiz Questions */
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              {/* Progress Bar */}
              <div className="bg-gray-100 h-2">
                <div 
                  className="bg-orange h-2 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="p-8">
                {/* Question Counter */}
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm text-gray-500">
                    Question {currentStep + 1} of {totalQuestions}
                  </span>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    ~2 min remaining
                  </div>
                </div>

                {/* Question */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center">
                      <currentQuestion.icon className="w-6 h-6 text-orange" />
                    </div>
                    <h2 className="font-heading text-xl font-semibold text-navy">
                      {currentQuestion.question}
                    </h2>
                  </div>

                  {/* Options */}
                  <div className="space-y-3">
                    {currentQuestion.options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(currentQuestion.id, option)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          currentAnswer?.value === option.value
                            ? 'border-orange bg-orange/5'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            currentAnswer?.value === option.value
                              ? 'border-orange bg-orange'
                              : 'border-gray-300'
                          }`}>
                            {currentAnswer?.value === option.value && (
                              <CheckCircle className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className={`font-medium ${
                            currentAnswer?.value === option.value ? 'text-navy' : 'text-gray-700'
                          }`}>
                            {option.label}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className="text-gray-600"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>

                  <Button
                    onClick={handleNext}
                    disabled={!currentAnswer}
                    className="bg-orange hover:bg-orange-600 text-white"
                  >
                    {currentStep === totalQuestions - 1 ? 'Get Results' : 'Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Contact Form */
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="w-8 h-8 text-orange" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-navy mb-2">
                  Almost Done!
                </h2>
                <p className="text-gray-600">
                  Enter your information to see your personalized assessment results and recommendations.
                </p>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={contactInfo.name}
                      onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="pl-10"
                      placeholder="John Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      required
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="tel"
                      required
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      required
                      value={contactInfo.city}
                      onChange={(e) => setContactInfo(prev => ({ ...prev, city: e.target.value }))}
                      className="pl-10"
                      placeholder="Minneapolis"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-orange hover:bg-orange-600 text-white py-6 text-lg"
                  >
                    {submitting ? 'Processing...' : 'See My Results'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  Your information is secure and will only be used to provide your assessment results 
                  and connect you with trusted local professionals if needed.
                </p>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-2 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to questions
                </button>
              </div>
            </div>
          )
        ) : (
          /* Results */
          <div className="space-y-6">
            {/* Main Result Card */}
            <div className={`${result.bgColor} ${result.borderColor} border-2 rounded-2xl p-8`}>
              <div className="flex items-start gap-4 mb-6">
                <div className={`w-16 h-16 rounded-full ${result.bgColor} flex items-center justify-center`}>
                  <result.icon className={`w-8 h-8 ${result.iconColor}`} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-navy mb-2">
                    {result.title}
                  </h2>
                  <p className="text-gray-600">
                    {result.description}
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-heading font-semibold text-navy mb-4">Our Recommendations:</h3>
                <ul className="space-y-3">
                  {result.recommendations.map((rec) => (
                    <li key={rec} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 ${result.iconColor} mt-0.5 flex-shrink-0`} />
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Answer Summary */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h3 className="font-heading font-semibold text-navy mb-4">Your Assessment Summary</h3>
              <div className="space-y-3">
                {QUESTIONS.map((q) => {
                  const answer = answers[q.id];
                  return (
                    <div key={q.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-600 text-sm">{q.question}</span>
                      <span className="text-navy font-medium text-sm">{answer?.label || 'Not answered'}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-navy rounded-2xl p-8 text-center">
              <h3 className="font-heading text-xl font-semibold text-white mb-3">
                Ready to Get Expert Help?
              </h3>
              <p className="text-gray-300 mb-6">
                Connect with trusted Minnesota roofing professionals for a free, no-obligation inspection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-orange hover:bg-orange-600 text-white">
                  <Link to="/service-areas">
                    Find Contractors in Your Area
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-gray-600 text-white hover:bg-white/10">
                  <Link to="/hail-map">
                    Check Recent Storm Activity
                  </Link>
                </Button>
              </div>
            </div>

            {/* Related Resources */}
            <div className="bg-gray-100 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-navy mb-4">Helpful Resources</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/storm-damage-restoration" className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow">
                  <h4 className="font-medium text-navy mb-1">Storm Damage Guide</h4>
                  <p className="text-sm text-gray-600">Learn about the restoration process</p>
                </Link>
                <Link to="/roof-replacement" className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow">
                  <h4 className="font-medium text-navy mb-1">Roof Replacement Guide</h4>
                  <p className="text-sm text-gray-600">What to know about replacing your roof</p>
                </Link>
                <Link to="/blog/storm-damage-insurance-claim-guide" className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow">
                  <h4 className="font-medium text-navy mb-1">Insurance Claim Tips</h4>
                  <p className="text-sm text-gray-600">Navigate the claims process</p>
                </Link>
                <Link to="/faqs" className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow">
                  <h4 className="font-medium text-navy mb-1">FAQs</h4>
                  <p className="text-sm text-gray-600">Common roofing questions answered</p>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
